<template>
  <section
    class="plan-container-wrapper"
    :class="{
      'recommended-plan': isRecommended,
      'show-recommended-styles': isRecommended,
      alternative: alternativeLayout
    }"
    :style="borderStyle"
  >
    <Shimmer :border-radius="borderRadiusShimmer" :loading="loading">
      <div>
        <div>
          <div v-if="lifetime && selectedPlan.discount" class="discount-badge">
            -{{ selectedPlan.discount }}%
          </div>
          <p
            v-if="isRecommended"
            class="recommended-plan-header text-center"
            :style="`color: ${prismicPropsObj.secondaryColor}`"
          >
            {{ $t('plans.common.cards.recommended') }}
          </p>
        </div>
        <div class="plan-container flex-column" :class="{ highlighted }">
          <!-- eslint-enable max-len -->
          <b-row>
            <b-col class="title" :style="`color: ${prismicPropsObj.primaryColor}`">
              <p>{{ selectedPlan.name }}</p>
              <p
                v-if="
                  selectedPlan.experienceType !== getDesign &&
                  selectedPlan.subtitle &&
                  !alternativeLayout
                "
                class="plan-subtitle"
              >
                {{ selectedPlan.subtitle }}
              </p>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              class="d-flex align-items-center justify-content-center flex-column"
              :class="{ 'no-switch': !showSwitch }"
            >
              <b-row class="align-items-baseline">
                <div class="currency align-self-center mr-1">
                  {{ selectedPlan.currencySymbol }}
                </div>
                <div class="price">{{ priceBigNumber }}</div>
                <div class="price small">{{ priceSmallNumber }}</div>
                <div v-show="!valueSwitch && showSwitch" class="per-period">
                  {{ $t('plans.common.cards.perMonth') }}
                </div>
              </b-row>
              <b-row
                v-if="prismicPropsObj.showPreviousPrice"
                class="previous-cost"
                :style="{ '--borderColor': prismicPropsObj.primaryColor }"
              >
                <span>$</span><span>{{ previousPrice }}</span>
              </b-row>

              <b-row v-if="valueSwitch && showSwitch" class="billed">
                <span
                  >{{ `$${selectedPlan.cost} ${$t('plans.common.cards.billedAnnualy')} ` }}
                </span>
              </b-row>
              <b-row v-if="lifetime" class="billed">
                <span>
                  {{ $t('plans.common.cards.billedLifetime') }}
                </span>
              </b-row>
            </b-col>
          </b-row>
          <b-row class="justify-content-center subscribe-container">
            <b-col class="button-container" cols="11">
              <Button v-bind="buttonProps" @click="subscribeClick">
                {{ buttonText }}
              </Button>
            </b-col>
          </b-row>
          <b-row v-if="isSingleLicense && isDesignPlan" class="justify-content-center">
            <Checkbox @input="extended = !extended">
              {{ $t('plans.common.cards.forMerchandiseUse') }}
            </Checkbox>
          </b-row>

          <b-row v-if="hasSpecs" class="mt-2 justify-content-center">
            <b-col cols="11" class="specs d-flex flex-column align-items-start">
              <component
                :is="i < shownSpecsAmount ? 'div' : 'b-collapse'"
                v-for="(s, i) in selectedPlan.specs || []"
                :id="i < shownSpecsAmount ? '' : 'collapse-hidden-' + _uid"
                :key="i"
                v-model="showDetail"
                class="row mt-2"
              >
                <b-col cols="1">
                  <Icon
                    icon="tick"
                    :width="alternativeLayout ? 18 : 10"
                    :height="alternativeLayout ? 18 : 10"
                    :color="tickIconColor"
                    :class="{ 'icon-spec': alternativeLayout }"
                    :exp-active="true"
                    :style="
                      alternativeLayout
                        ? isRecommended
                          ? 'background: #FFF'
                          : 'background: #2E2E2E'
                        : ''
                    "
                  />
                </b-col>
                <b-col class="text-left spec">
                  {{ s.spec }}
                  <b-button
                    v-if="s.tooltip"
                    :id="`btn-tooltip-${selectedPlan.id}-${i}`"
                    class="btn-tooltip ml-1"
                  >
                    i
                  </b-button>
                </b-col>
                <b-tooltip
                  v-if="s.tooltip"
                  custom-class="custom-tooltip"
                  :target="`btn-tooltip-${selectedPlan.id}-${i}`"
                  placement="top"
                >
                  {{ s.tooltip }}
                </b-tooltip>
              </component>
            </b-col>
          </b-row>
          <b-row v-if="!alternativeLayout" class="mt-4 justify-content-center">
            <b-col cols="11" class="specs d-flex flex-column align-items-start">
              <b-row>
                <b-col>
                  <span
                    v-b-toggle="'collapse-hidden-' + _uid"
                    class="details cursor-pointer"
                    :style="`color: ${prismicPropsObj.tertiaryColor}`"
                    @click="$ga.event('All Plans', 'Click See More Details', $route.fullPath)"
                  >
                    {{
                      showDetail
                        ? $t('plans.common.cards.seeLessDetails')
                        : $t('plans.common.cards.seeMoreDetails')
                    }}
                  </span>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row
            v-if="!alternativeLayout && prismicPropsObj.showBenefitsText"
            class="my-2 justify-content-center"
          >
            <b-col
              cols="11"
              class="specs cursor-pointer d-flex flex-column align-items-start"
              :style="`color: ${prismicPropsObj.primaryColor}`"
              @click="goToTable"
            >
              {{ $t('plans.common.cards.blackFridayBenefits') }}
            </b-col>
          </b-row>
          <b-row
            v-if="!alternativeLayout && selectedPlan.image"
            class="mt-4 justify-content-center"
          >
            <b-col cols="11">
              <img
                :src="selectedPlan.image"
                :alt="$t('plans.common.cards.PODlogosAlt')"
                class="mt-2 w-100 filter-grayscale"
                loading="lazy"
              />
            </b-col>
          </b-row>
        </div>
      </div>
    </Shimmer>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from 'UI/Button';
import Checkbox from 'UI/Checkbox';
import Icon from 'UI/Icon';
import { getWindowScrollTop } from '~/utils/common.js';
import Shimmer from '@/components/PurchaseFlow/Plans/Shimmer';

export default {
  components: {
    Button,
    Checkbox,
    Icon,
    Shimmer
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    valueSwitch: {
      type: Boolean,
      required: false,
      default: true
    },
    individualVectorPlans: {
      type: Boolean,
      default: false
    },
    planIndex: {
      type: Number,
      required: false,
      default: 0
    },
    plansLength: {
      type: Number,
      required: false,
      default: 3
    },
    plan: {
      type: [Object, Number],
      required: true
    },
    isFreeUser: {
      type: Boolean,
      required: true
    },
    seeMoreLink: {
      type: String,
      required: false,
      default: ''
    },
    lifetime: {
      type: Boolean,
      default: false,
      required: false
    },
    isDesignPlan: {
      type: Boolean,
      default: false
    },
    prismicProps: {
      type: Object,
      default: () => ({})
    },
    customPlan: {
      type: Boolean,
      default: false,
      required: false
    },
    uId: {
      type: Number,
      required: true
    },
    alternativeLayout: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      shownSpecsAmount: 3,
      extended: false,
      showDetail: false,
      greenColor: '#3cd699'
    };
  },
  computed: {
    ...mapGetters('experience', ['getExperience', 'getDesign']),
    ...mapGetters('plans', ['getPlanToFetch']),
    buttonProps() {
      let props = {};
      if (this.isRecommended || this.lifetime) {
        props = {
          enabled: this.buttonEnabled,
          width: 'full',
          class: 'w-100',
          withGradient: true,
          gradient: this.greenColor
        };
        if (
          this.prismicPropsObj.buttonGradientColor1 &&
          this.prismicPropsObj.buttonGradientColor2
        ) {
          props.withGradient = true;
          props.gradient =
            'transparent linear-gradient(85deg, ' +
            this.prismicPropsObj.buttonGradientColor1 +
            ' 0%, ' +
            this.prismicPropsObj.buttonGradientColor2 +
            ' 100%) 0% 0% no-repeat padding-box;';
        }
      } else {
        props = {
          enabled: this.buttonEnabled,
          width: 'full',
          class: 'w-100',
          shape: 'outline',
          color: 'transparent',
          text: this.buttonTextColor,
          fontColor: this.prismicPropsObj.tertiaryColor || this.greenColor,
          withGradient: this.alternativeLayout
        };
        if (this.alternativeLayout) {
          props.withGradient = true;
          props.gradient = // eslint-disable-next-line max-len
            'transparent linear-gradient(255deg, #5287F2 0%, #A27BFF 100%) 0% 0% no-repeat padding-box;';
          props.fontColor = this.prismicPropsObj.tertiaryColor || '#FFF' || this.greenColor;
        }
      }
      return props;
    },
    borderStyle() {
      return this.isRecommended && !this.alternativeLayout
        ? `border: 2px solid ${this.prismicPropsObj.primaryColor || this.greenColor}`
        : '';
    },
    prismicPropsObj() {
      return this.prismicProps || {};
    },
    previousPrice() {
      return this.prismicPropsObj.previousPrice[this.planIndex];
    },
    isRecommended() {
      return this.planIndex == 1 && !this.lifetime && this.plansLength === 3;
    },
    highlighted() {
      return this.selectedPlan ? this.selectedPlan.highlighted : false;
    },
    selectedPlan() {
      if (this.plan) {
        if (this.valueSwitch && this.plan.annual) {
          return this.plan.annual;
        } else if (!this.valueSwitch && this.plan.monthly) {
          return this.plan.monthly;
        } else if (this.extended && this.plan.extended) {
          return this.plan.extended;
        } else if (!this.extended && this.plan.simple) {
          return this.plan.simple;
        } else if (this.lifetime || this.customPlan) {
          return this.plan;
        } else {
          return {};
        }
      } else {
        return {};
      }
    },
    showSwitch() {
      if (this.isSingleLicense || this.lifetime) {
        return false;
      } else {
        if (this.plan) {
          return this.plan.monthly && this.plan.annual;
        } else {
          return false;
        }
      }
    },
    hasSpecs() {
      return this.selectedPlan.specs && this.selectedPlan.specs.length > 0;
    },
    buttonEnabled() {
      return this.selectedPlan.upgrade || this.isSingleLicense;
    },
    buttonTextColor() {
      return !this.alternativeLayout
        ? this.buttonEnabled
          ? this.prismicPropsObj.tertiaryColor || 'green'
          : 'white'
        : 'white';
    },
    recommendedButtonTextColor() {
      return this.prismicPropsObj.buttonGradientColor1
        ? this.prismicPropsObj.buttonGradientColor1
        : '';
    },
    buttonText() {
      if (this.isSingleLicense) {
        return this.$t('plans.common.cards.buy');
      } else {
        if (this.$auth.loggedIn && !this.isFreeUser && this.selectedPlan.upgrade) {
          return this.$t('plans.common.cards.upgrade');
        } else {
          return this.$t('plans.common.cards.suscribe');
        }
      }
    },
    isSingleLicense() {
      return this.plan && (this.plan.simple || this.plan.extended) ? true : false;
    },
    isPlansPage() {
      const isDesignPlans = this.$urlNameChecker('design-plans');
      const isMerchPlans = this.$urlNameChecker('merch-plans');
      return isDesignPlans || isMerchPlans;
    },
    shownPrice() {
      if (this.lifetime) return [this.selectedPlan.cost, ''];
      var price = this.selectedPlan.cost;
      if (this.valueSwitch && !this.isSingleLicense) {
        price = this.selectedPlan.cost / 12;
      }
      const noDecimals = Math.floor(price);
      const decimals = price - noDecimals;
      if (decimals > 0.1 && decimals <= 0.5) {
        return [noDecimals, '.50'];
      } else if (decimals > 0.5) {
        return [noDecimals, '.99'];
      } else {
        return [noDecimals];
      }
    },
    priceBigNumber() {
      return this.shownPrice[0];
    },
    priceSmallNumber() {
      return this.shownPrice.length == 2 ? this.shownPrice[1] : '';
    },
    tickIconColor() {
      return this.alternativeLayout ? (this.isRecommended ? '#5287F2' : '#FFF') : '#a2a2a2';
    },
    borderRadiusShimmer() {
      const alternativeBorders = ['0', '15px', '0 15px 15px 0'];
      return this.alternativeLayout ? alternativeBorders[this.planIndex] : '15px';
    }
  },
  methods: {
    subscribeClick() {
      this.$emit('subscribe-click', this.selectedPlan);
      this.$ga.event('All Plans', 'Click Button Subscribe Top Plans', this.$route.fullPath);
    },
    async goToTable() {
      const element = document.getElementById('compare-plans');
      if (element) {
        const hideMenuSeasonal = await this.$cookies.get('vx.hideMenuSeasonal');
        const headerOffset = hideMenuSeasonal ? 120 : 260;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + getWindowScrollTop() - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.custom-tooltip {
  margin: 10px;
  .tooltip-inner {
    padding: 10px;
  }
}
.plan-container-wrapper {
  box-shadow: 0px 0px 40px -10px rgba(0, 0, 0, 0.1);
  border-radius: 15px;
  background-color: white;
  display: flex;
  min-height: 34rem;
  flex-direction: column;
  align-items: center;
  margin: calc(10px + 1.5em) 0 1em;
  flex: 0 0 30%;
  &.show-recommended-styles {
    width: 31%;
  }
  @include lg {
    flex: 0 0 45%;
    width: 45% !important;
  }
  @include md {
    flex: 0 0 100%;
    width: 100% !important;
  }
  &.recommended-plan {
    margin: 0;
    .plan-container {
      padding: 0.7rem 2rem 2rem;
    }
  }
  .recommended-plan-header {
    color: #3cd699;
    letter-spacing: 4px;
    font-size: 14px;
    margin: 25px 0 0;
  }
  .discount-badge {
    background-color: var(--exp-color);
    color: white;
    padding: 3px 8px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    z-index: 1;
  }
  .spec {
    padding-left: 0;
  }
  &.alternative {
    min-height: 26rem;
    margin: 0;
    box-shadow: none;
    min-width: 280px;
    @include lg {
      width: 100%;
      flex: unset;
    }
    .icon-spec {
      border-radius: 6px;
    }
    .spec {
      padding-left: 12px;
      font-size: 13px;
    }
    .recommended-plan-header {
      display: none;
    }
    .plan-container {
      .subscribe-container {
        margin: 20px 0;
        .button-container {
          width: 100%;
          padding: 0;
        }
      }
    }
    &.recommended-plan {
      .plan-container {
        padding: 1.5rem 2rem 2rem;
        .title {
          padding-top: 15px;
        }
      }
    }
    &.show-recommended-styles {
      position: relative;
      background: transparent linear-gradient(209deg, #5287f2 0%, #a180ff 100%) 0% 0% no-repeat
        padding-box;
      margin-top: -15px;
      margin-bottom: -15px;
      .title,
      .billed,
      .specs,
      .currency,
      .price,
      .per-period {
        color: white;
      }
    }
  }
}
.plan-container {
  position: relative;
  display: flex;
  min-height: 100%;
  height: max-content;
  padding: 1.5rem 2rem 2rem;
  border-radius: 5px;
  text-align: center;
  color: rgba(0, 0, 0, 0.1);
  width: 100%;
  &.highlighted {
    color: rgba(32, 185, 181, 0.4);
  }
  .title {
    letter-spacing: 1px;
    font-size: 22px;
    font-weight: 500;
    color: $mediumDarkGray;
    text-transform: uppercase;
    p {
      margin-bottom: 8px;
    }
    .plan-subtitle {
      font-size: 12px;
      text-transform: none;
      color: $gray;
      margin-bottom: 0px;
    }
  }
  .billed {
    color: $mediumDarkGray;
    height: 24px;
    font-size: 13px;
    &.is-seasonal {
      text-transform: uppercase;
    }
  }
  .currency {
    font-size: 25px;
    color: $mediumDarkGray;
  }
  .price {
    font-size: 70px;
    color: $mediumDarkGray;
  }
  .price.small {
    font-size: 40px;
    align-self: baseline;
    margin-top: 15px;
  }
  .per-period {
    color: $mediumDarkGray;
  }
  .specs {
    color: gray;
    font-size: 12px;
    .btn-tooltip {
      background-color: #3cd699;
      border: none;
      height: 20px;
      width: 20px;
      border-radius: 200%;
      padding: 0;
      font-weight: 500;
      font-size: 13px;
    }
  }
  .details {
    color: $gray;
    font-weight: 400;
    font-size: 12px;
    border-bottom: 2px solid;
  }
  .no-switch {
    margin-top: 0px;
  }
  .subscribe-container {
    margin-top: 25px;
    margin-bottom: 48px;
    .button-container {
      align-self: flex-end;
    }
  }
  .previous-cost {
    color: $mediumDarkGray;
    position: relative;
    height: 100%;
    padding: 0 5px;
    &:before {
      border-bottom: 2px solid var(--borderColor);
      position: absolute;
      left: 0;
      content: '';
      width: 100%;
      height: 50%;
    }
  }
}

.filter-grayscale {
  filter: grayscale(1);
}
</style>
